import type { Stop } from '@/types'

/**
 * Grid index over the 4,991 stops.
 *
 * The home map cannot mount every stop, so it renders only what is inside the
 * current viewport, recomputed on every pan. A linear scan of 4,991 records per
 * pan is affordable exactly once and then stops being affordable as soon as
 * anything else runs in the same frame. Bucketing by cell makes a bounds query
 * touch only the covered cells.
 *
 * 0.01° ≈ 1.1 km, which puts a typical city-zoom viewport across a handful of
 * cells and keeps most buckets small.
 */
const CELL_SIZE = 0.01

export interface StopIndex {
  queryBounds(bounds: LatLngBounds): Stop[]
  queryNearest(lat: number, lng: number, radiusMeters: number): Stop[]
}

export interface LatLngBounds {
  south: number
  west: number
  north: number
  east: number
}

function cellKey(lat: number, lng: number): string {
  return `${Math.floor(lat / CELL_SIZE)}:${Math.floor(lng / CELL_SIZE)}`
}

export function createStopIndex(stops: Stop[]): StopIndex {
  const cells = new Map<string, Stop[]>()

  for (const stop of stops) {
    const key = cellKey(stop.lat, stop.lng)
    const bucket = cells.get(key)
    if (bucket) bucket.push(stop)
    else cells.set(key, [stop])
  }

  function withinBounds(stop: Stop, bounds: LatLngBounds): boolean {
    return (
      stop.lat >= bounds.south &&
      stop.lat <= bounds.north &&
      stop.lng >= bounds.west &&
      stop.lng <= bounds.east
    )
  }

  function collect(bounds: LatLngBounds): Stop[] {
    const found: Stop[] = []
    const latStart = Math.floor(bounds.south / CELL_SIZE)
    const latEnd = Math.floor(bounds.north / CELL_SIZE)
    const lngStart = Math.floor(bounds.west / CELL_SIZE)
    const lngEnd = Math.floor(bounds.east / CELL_SIZE)

    /**
     * The cell loop costs O(cells covered), not O(stops), so wide bounds are
     * catastrophically slower than the naive scan the index exists to avoid:
     * whole-world bounds span ~162 million cells, nearly all empty, and hang the
     * caller. A zoom floor in the UI is not a sufficient guard — that is a
     * caller-side convention, and this needs to be safe for any input.
     *
     * When the covered area exceeds the number of stops, scanning every stop is
     * strictly cheaper. This makes the index never worse than no index.
     */
    const cellCount = (latEnd - latStart + 1) * (lngEnd - lngStart + 1)
    if (!Number.isFinite(cellCount) || cellCount > stops.length) {
      return stops.filter((stop) => withinBounds(stop, bounds))
    }

    for (let latCell = latStart; latCell <= latEnd; latCell += 1) {
      for (let lngCell = lngStart; lngCell <= lngEnd; lngCell += 1) {
        const bucket = cells.get(`${latCell}:${lngCell}`)
        if (!bucket) continue
        // Cells on the edge of the range straddle the bounds, so their contents
        // still have to be tested individually.
        for (const stop of bucket) {
          if (withinBounds(stop, bounds)) found.push(stop)
        }
      }
    }

    return found
  }

  return {
    queryBounds: collect,

    queryNearest(lat, lng, radiusMeters) {
      // Degrees of longitude shrink with latitude; at Hanoi's ~21°N that is a
      // ~7% difference. Ignoring it would make the search box measurably
      // narrower than intended in the east-west direction.
      const latDelta = radiusMeters / 111_320
      const lngDelta = radiusMeters / (111_320 * Math.cos((lat * Math.PI) / 180))

      const candidates = collect({
        south: lat - latDelta,
        north: lat + latDelta,
        west: lng - lngDelta,
        east: lng + lngDelta,
      })

      return candidates
        .map((stop) => ({ stop, distance: haversineMeters(lat, lng, stop.lat, stop.lng) }))
        .filter((entry) => entry.distance <= radiusMeters)
        .sort((a, b) => a.distance - b.distance)
        .map((entry) => entry.stop)
    },
  }
}

const EARTH_RADIUS_M = 6_371_000

export function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180
  const dLat = toRadians(lat2 - lat1)
  const dLng = toRadians(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a))
}
